import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState, type FormEvent } from "react";
import { workClassLabels, type WorkClassification } from "../domain/operations";
import { isActiveInOperations } from "../domain/operationsQueries";
import { BrandLockup } from "../components/BrandLockup";
import { Modal } from "../components/Modal";
import { AppThemeToggle } from "../components/AppThemeToggle";
import { SidebarAccountControl } from "../account/SidebarAccountControl";
import { MobileAccountTrigger } from "../account/MobileAccountMenu";
import { useOperations } from "../state/OperationsProvider";

export type AppView = "today" | "ahead" | "events" | "attention" | "horizon" | "more";

const desktopNav: { id: AppView; to: string; label: string; icon: string }[] = [
  { id: "today", to: "/today", label: "Today", icon: "⌂" },
  { id: "ahead", to: "/look-ahead", label: "Look Ahead", icon: "↗" },
  { id: "events", to: "/events", label: "Events", icon: "▣" },
  { id: "attention", to: "/attention", label: "Attention", icon: "!" },
  { id: "horizon", to: "/horizon", label: "Horizon", icon: "◎" },
];

const mobileNav: { id: AppView; to: string; label: string; icon: string }[] = [
  { id: "today", to: "/today", label: "Today", icon: "⌂" },
  { id: "events", to: "/events", label: "Events", icon: "▣" },
  { id: "attention", to: "/attention", label: "Attention", icon: "!" },
  { id: "more", to: "/more", label: "More", icon: "•••" },
];

function viewFromPath(pathname: string): AppView {
  if (pathname.startsWith("/look-ahead")) return "ahead";
  if (pathname.startsWith("/events")) return "events";
  if (pathname.startsWith("/attention")) return "attention";
  if (pathname.startsWith("/horizon")) return "horizon";
  if (pathname.startsWith("/more")) return "more";
  return "today";
}

export function AppShell() {
  const { snapshot, toast, announce, addWork } = useOperations();
  const location = useLocation();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const view = viewFromPath(location.pathname);
  const createEventViaImport =
    location.pathname === "/events" || location.pathname.startsWith("/events/import");
  const attentionCount = snapshot?.attention.filter((item) => !item.resolved && snapshot.events.find((event) => event.id === item.eventId)?.status !== "cancelled").length ?? 0;
  const activeEvents = snapshot?.events.filter(isActiveInOperations) ?? [];

  function submitWork(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const title = String(data.get("title") ?? "").trim();
    if (!title) return;
    addWork({ title, eventId: String(data.get("eventId")), classification: String(data.get("classification")) as WorkClassification });
    setAddOpen(false);
  }

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to operations</a>
      <aside className="sidebar" aria-label="Primary navigation">
        <BrandLockup />
        <nav>
          {desktopNav.map((item) => (
            <NavLink key={item.id} to={item.to} className={({ isActive }) => `nav-item ${isActive || (item.id === "events" && location.pathname.startsWith("/events")) ? "active" : ""}`} aria-current={view === item.id ? "page" : undefined}>
              <span className="nav-icon" aria-hidden="true">{item.icon}</span>{item.label}
              {item.id === "attention" && attentionCount > 0 && <span className="nav-count">{attentionCount}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-spacer" />
        <div className="shift-card">
          <div className="eyebrow">Current shift</div><strong>Morning Operations</strong><span>6:00 AM – 2:00 PM</span>
          <div className="people-stack" aria-label="Four staff on shift"><i>HA</i><i>JM</i><i>RL</i><i>+1</i></div>
        </div>
        <SidebarAccountControl />
      </aside>

      <main id="main-content">
        <header className="desktop-header">
          <div><div className="date-line"><span>Operations</span><span>•</span><span>Friday, September 12</span></div><h1>{desktopNav.find((item) => item.id === view)?.label ?? "Today"}</h1></div>
          <div className="header-actions">
            <AppThemeToggle className="theme-toggle-header" />
            <button className="icon-button" aria-label="Search" onClick={() => announce("Search is ready for data integration")}>⌕</button>
            <button className="icon-button notification" aria-label={`${attentionCount} notifications`} onClick={() => navigate("/attention")}>♢{attentionCount > 0 && <i />}</button>
            <button
              className="primary-button"
              onClick={() => (createEventViaImport ? navigate("/events/import") : setAddOpen(true))}
            >
              <span aria-hidden="true">＋</span> {createEventViaImport ? "New Event" : "Add work"}
            </button>
          </div>
        </header>
        <header className="mobile-header">
          <BrandLockup />
          <div className="mobile-head-actions">
            <AppThemeToggle />
            <button aria-label="Search" onClick={() => announce("Search is ready for data integration")}>⌕</button>
            <button className="notification" aria-label={`${attentionCount} notifications`} onClick={() => navigate("/attention")}>♢{attentionCount > 0 && <i />}</button>
            <MobileAccountTrigger />
          </div>
        </header>
        <Outlet />
      </main>

      <nav className="bottom-nav" aria-label="Mobile navigation">
        {mobileNav.map((item) => (
          <NavLink key={item.id} to={item.to} className={({ isActive }) => isActive || (item.id === "events" && location.pathname.startsWith("/events")) ? "active" : ""} aria-current={view === item.id ? "page" : undefined}>
            <span className={item.id === "attention" && attentionCount > 0 ? "nav-alert" : ""}>{item.icon}</span><small>{item.label}</small>
          </NavLink>
        ))}
      </nav>
      <div className={`toast ${toast ? "show" : ""}`} role="status" aria-live="polite">{toast}</div>
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add work" description="Create a quick task and place it into today's operation.">
        <form className="quick-form" onSubmit={submitWork}>
          <label>Task<input name="title" required autoFocus placeholder="What needs to happen?" /></label>
          <label>Event<select name="eventId">{activeEvents.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label>Work class<select name="classification">{(Object.keys(workClassLabels) as WorkClassification[]).map((key) => <option key={key} value={key}>{workClassLabels[key]}</option>)}</select></label>
          <button type="submit">Add to Today</button>
        </form>
      </Modal>
    </div>
  );
}
